/*
 Навешивает обработчики на header внутри переданного контейнера.
 Реализует горизонтальное перетаскивание окон с предпросмотром смены мест.
 Поддерживает перетаскивание через несколько окон одновременно.
 Возвращает функцию очистки.
*/

const attachedHeaders = new WeakSet<HTMLElement>();

export const dragToReorder = (
	container: HTMLElement | null,
	onReorder: (
		fromIndex: number,
		toIndex: number,
		oldPositions: Map<HTMLElement, number>,
	) => void,
): (() => void) => {
	if (!container) return () => {};

	const getPaneEls = () =>
		Array.from(
			container.querySelectorAll<HTMLElement>(":scope > .split-pane__pane"),
		);

	const removers: Array<() => void> = [];

	const setupHeader = (el: HTMLElement, getIndex: () => number) => {
		const header = el.querySelector<HTMLElement>(".window__header");
		if (!header) return;
		if (attachedHeaders.has(header)) return;
		attachedHeaders.add(header);

		let startX = 0;
		let draggedIdx = -1;
		let pendingTarget: number | null = null;
		let originalRects: DOMRect[] = [];
		let isDragging = false;

		// Множество индексов панелей, которые сейчас сдвинуты
		let displacedSet = new Set<number>();

		const resetAll = () => {
			displacedSet = new Set();
			getPaneEls().forEach((pane) => {
				pane.style.transform = "";
				pane.style.transition = "";
				pane.classList.remove(
					"split-pane__pane--dragging",
					"split-pane__pane--swap-target",
				);
			});
		};

		const onMouseMove = (e: MouseEvent) => {
			if (!isDragging) return;

			const deltaX = e.clientX - startX;
			const paneEls = getPaneEls();

			// Двигаем перетаскиваемое окно
			const dragged = paneEls[draggedIdx];
			if (!dragged) return;
			dragged.style.transform = `translateX(${deltaX}px)`;
			dragged.style.transition = "none";

			const draggedRect = originalRects[draggedIdx];
			const draggedVisualLeft = draggedRect.left + deltaX;
			const draggedVisualRight = draggedVisualLeft + draggedRect.width;
			const slideAmount = draggedRect.width;

			// Перебираем все нетаскаемые панели.
			// Условие сдвига: визуальный край перетаскиваемого окна прошёл центр панели i.
			// Это математически эквивалентно ≥50% перекрытию и монотонно по deltaX —
			// поэтому панель остаётся сдвинутой пока окно тянут дальше.
			let bestTarget: number | null = null;
			const newDisplacedSet = new Set<number>();

			for (let i = 0; i < originalRects.length; i++) {
				if (i === draggedIdx) continue;
				const r = originalRects[i];
				const pane = paneEls[i];
				if (!pane) continue;

				const center = r.left + r.width * 0.5;
				let displaced = false;
				let slideDir = 0;

				if (i > draggedIdx) {
					// Панель справа: сдвигается влево когда правый край перетаскиваемого прошёл её центр
					if (draggedVisualRight >= center) {
						displaced = true;
						slideDir = -1;
						if (bestTarget === null || i > bestTarget) bestTarget = i;
					}
				} else {
					// Панель слева: сдвигается вправо когда левый край перетаскиваемого прошёл её центр
					if (draggedVisualLeft <= center) {
						displaced = true;
						slideDir = 1;
						if (bestTarget === null || i < bestTarget) bestTarget = i;
					}
				}

				if (displaced) {
					newDisplacedSet.add(i);
					const targetOffset = slideDir * slideAmount;
					pane.classList.add("split-pane__pane--swap-target");
					pane.style.transition = "transform 150ms ease";
					if (!displacedSet.has(i)) {
						// Первый сдвиг: принудительный reflow чтобы браузер зафиксировал
						// текущее состояние (transform = "") ДО применения нового значения,
						// иначе transition и transform попадают в один кадр и анимации нет
						void pane.offsetWidth;
					}
					pane.style.transform = `translateX(${targetOffset}px)`;
				} else {
					// Панель возвращается на место
					if (displacedSet.has(i)) {
						pane.classList.remove("split-pane__pane--swap-target");
						pane.style.transition = "transform 150ms ease";
						pane.style.transform = "";
					}
				}
			}

			displacedSet = newDisplacedSet;
			pendingTarget = bestTarget;
		};

		const onMouseUp = () => {
			if (!isDragging) return;
			isDragging = false;

			document.body.style.userSelect = "";
			window.removeEventListener("mousemove", onMouseMove);
			window.removeEventListener("mouseup", onMouseUp);

			const paneEls = getPaneEls();
			const capturedDraggedIdx = draggedIdx;
			const capturedPendingTarget = pendingTarget;

			// Сбрасываем состояние до любых DOM-операций
			pendingTarget = null;
			draggedIdx = -1;
			originalRects = [];

			if (
				capturedPendingTarget !== null &&
				capturedPendingTarget !== capturedDraggedIdx
			) {
				// SWAP: помечаем перетаскиваемый элемент чтобы он оставался поверх
				// других окон во время FLIP-анимации (класс снимается в useLayoutEffect)
				paneEls[capturedDraggedIdx]?.classList.add(
					"split-pane__pane--flip-dragged",
				);

				// Фиксируем визуальные позиции (с учётом transform) ДО сброса,
				// передаём в React для FLIP-анимации
				const oldPositions = new Map<HTMLElement, number>();
				paneEls.forEach((el) =>
					oldPositions.set(el, el.getBoundingClientRect().left),
				);

				resetAll();
				onReorder(capturedDraggedIdx, capturedPendingTarget, oldPositions);
			} else {
				// NO SWAP: плавно возвращаем перетаскиваемое окно на место
				const dragged = paneEls[capturedDraggedIdx];
				if (dragged) {
					dragged.style.transition = "transform 200ms ease";
					dragged.style.transform = "";
					setTimeout(() => {
						dragged.style.transition = "";
						dragged.classList.remove("split-pane__pane--dragging");
					}, 210);
				}
				displacedSet = new Set();
			}
		};

		const onMouseDown = (e: MouseEvent) => {
			// Игнорируем клики по кнопкам внутри header
			if ((e.target as HTMLElement).closest("button, a, input, select")) return;
			// Только левая кнопка мыши
			if (e.button !== 0) return;

			// Защита от зависшего состояния: сбрасываем если предыдущий drag не завершился
			if (isDragging) {
				isDragging = false;
				document.body.style.userSelect = "";
				resetAll();
			}

			const paneEls = getPaneEls();
			draggedIdx = getIndex();
			if (draggedIdx === -1) return;

			e.preventDefault();
			isDragging = true;
			startX = e.clientX;
			pendingTarget = null;
			displacedSet = new Set();

			// Снимок позиций всех панелей до начала перетаскивания
			originalRects = paneEls.map((p) => p.getBoundingClientRect());

			paneEls[draggedIdx]?.classList.add("split-pane__pane--dragging");

			document.body.style.userSelect = "none";

			window.addEventListener("mousemove", onMouseMove);
			window.addEventListener("mouseup", onMouseUp);
		};

		header.addEventListener("mousedown", onMouseDown);

		removers.push(() => {
			header.removeEventListener("mousedown", onMouseDown);
			attachedHeaders.delete(header);
		});
	};

	// Навешиваем обработчики на все текущие панели
	const paneEls = getPaneEls();
	paneEls.forEach((el) => {
		setupHeader(el, () => getPaneEls().indexOf(el));
	});

	return () => {
		removers.forEach((fn) => fn());
	};
};
